/**
 * Author: Fleming Shi
 * Project: DAL (Digital Abstraction Layer)
 * Date: 2025-12-12
 * Description: Business Logic management page
 */

import React, { useState, useEffect } from 'react'
import { Code, Plus, Search, Edit, Trash2, Eye, RefreshCw } from 'lucide-react'
import { toast } from 'react-toastify'

interface BusinessLogicItem {
  id: number
  name: string
  description: string
  systemId: number
  systemName: string
  logicType: string
  status: string
  createdAt: string
  updatedAt: string
}

const BusinessLogic: React.FC = () => {
  const [logicItems, setLogicItems] = useState<BusinessLogicItem[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    fetchBusinessLogic()
  }, [])

  const fetchBusinessLogic = async () => {
    try {
      setLoading(true)
      // TODO: Replace with actual API call
      // const response = await api.get('/business-logic');
      // setLogicItems(response.data);

      // Mock data for now
      setLogicItems([
        {
          id: 1,
          name: 'Payment Processing',
          description: 'Handles payment transactions and validations',
          systemId: 1,
          systemName: 'E-Commerce Platform',
          logicType: 'Transaction',
          status: 'Active',
          createdAt: '2025-12-01',
          updatedAt: '2025-12-12',
        },
        {
          id: 2,
          name: 'Order Validation',
          description: 'Validates order data before processing',
          systemId: 1,
          systemName: 'E-Commerce Platform',
          logicType: 'Validation',
          status: 'Active',
          createdAt: '2025-12-05',
          updatedAt: '2025-12-10',
        },
      ])
    } catch (error) {
      console.error('Error fetching business logic:', error)
      toast.error('Failed to load business logic')
    } finally {
      setLoading(false)
    }
  }

  const filteredLogic = logicItems.filter(
    (item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.systemName.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  return (
    <div className="mx-auto max-w-7xl p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="flex items-center gap-2 text-3xl font-bold text-gray-900 dark:text-gray-100">
              <Code className="text-primary-600 h-8 w-8" />
              Business Logic
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Manage and document business logic across systems</p>
          </div>
          <button
            onClick={() => toast.info('Create business logic - Coming soon')}
            className="bg-primary-600 hover:bg-primary-700 flex cursor-pointer items-center gap-2 rounded-lg px-4 py-2 text-white transition-colors"
          >
            <Plus className="h-5 w-5" />
            Add Business Logic
          </button>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 transform text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="Search business logic..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="focus:ring-primary-500 w-full rounded-lg border border-gray-300 py-2 pr-4 pl-10 focus:border-transparent focus:ring-2 dark:border-gray-600"
            />
          </div>
          <button
            onClick={fetchBusinessLogic}
            className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-900"
          >
            <RefreshCw className="h-5 w-5" />
            Refresh
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="border-primary-600 h-12 w-12 animate-spin rounded-full border-b-2"></div>
        </div>
      ) : (
        <>
          {/* Stats */}
          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:shadow-none">
              <div className="text-sm text-gray-600 dark:text-gray-400">Total Logic Items</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{logicItems.length}</div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:shadow-none">
              <div className="text-sm text-gray-600 dark:text-gray-400">Active</div>
              <div className="text-2xl font-bold text-green-600">
                {logicItems.filter((l) => l.status === 'Active').length}
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:shadow-none">
              <div className="text-sm text-gray-600 dark:text-gray-400">Validation Rules</div>
              <div className="text-2xl font-bold text-blue-600">
                {logicItems.filter((l) => l.logicType === 'Validation').length}
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:shadow-none">
              <div className="text-sm text-gray-600 dark:text-gray-400">Transactions</div>
              <div className="text-2xl font-bold text-purple-600">
                {logicItems.filter((l) => l.logicType === 'Transaction').length}
              </div>
            </div>
          </div>

          {/* Business Logic Table */}
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:shadow-none">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-white/5">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                    System
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                    Last Updated
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                {filteredLogic.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      {searchTerm
                        ? 'No business logic found matching your search.'
                        : 'No business logic items yet. Click "Add Business Logic" to get started.'}
                    </td>
                  </tr>
                ) : (
                  filteredLogic.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-900">
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{item.description}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-gray-100">{item.systemName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                          {item.logicType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-medium ${
                            item.status === 'Active'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                          }`}
                        >
                          {item.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
                        {new Date(item.updatedAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toast.info('View details - Coming soon')}
                            className="text-blue-600 hover:text-blue-900 dark:hover:text-blue-300"
                            title="View"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => toast.info('Edit - Coming soon')}
                            className="text-yellow-600 hover:text-yellow-900 dark:hover:text-yellow-300"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => toast.info('Delete - Coming soon')}
                            className="text-red-600 hover:text-red-900 dark:hover:text-red-300"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

export default BusinessLogic
